import { useDealSummaryController } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/DealSummary/useDealSummaryController';
import { renderMIPCreateSectionHeaderPortal } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/mipCreatePortals';
import { ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';
import {
	TEST_ELEMENT_IDS,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { Button } from '@dealstack/frontend/shared/components/fundament/Button/Button';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { NotificationBox } from '@dealstack/frontend/shared/components/NotificationBox/NotificationBox';
import { SectionHeader } from '@dealstack/frontend/shared/components/SectionHeader/SectionHeader';
import {
	useTranslationDefault,
	useTranslationMipCreate,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

interface Props {
	className?: string;
}

const DealSummary = ({ className }: Props) => {
	const { isLoading, isSubmitBlocked, onComplete, stepsStatuses } = useDealSummaryController();

	const tMipCreate = useTranslationMipCreate();
	const tDefault = useTranslationDefault();
	const navigate = useNavigate();

	return (
		<div className={className}>
			{renderMIPCreateSectionHeaderPortal(
				<SectionHeader title={tMipCreate('summary.header.title')} />
			)}
			<WarningWrapper>
				{stepsStatuses?.map((step, index) => {
					if (!step.status || !step.code) {
						return null;
					}
					return (
						<UIElementIdentifier
							key={step.step}
							identifier={TEST_ELEMENT_IDS.MIP_CREATE_NOTIFICATION_BOX}
							index={index}
						>
							<NotificationBox
								key={step.step}
								variant={step.status.includes('ERROR') ? 'error' : 'warning'}
								content={tDefault(`error_codes.${step.code}`)}
							/>
						</UIElementIdentifier>
					);
				})}
			</WarningWrapper>
			<ContentWrapper>
				<UIElementIdentifier
					identifier={TEST_ELEMENT_IDS.MIP_CREATE_DEAL_SUMMARY_COMPLETE_SETUP_BUTTON}
				>
					<Box onClick={onComplete}>
						<Text variant="p-lg">{tMipCreate('summary.complete.paragraph')}</Text>
						<Button
							disabled={isSubmitBlocked}
							variant="outlined"
							icon="chevron-right"
							iconPlacement="right"
							iconSize="lg"
						>
							{tMipCreate('summary.complete.button')}
						</Button>
					</Box>
				</UIElementIdentifier>
				<UIElementIdentifier
					identifier={TEST_ELEMENT_IDS.MIP_CREATE_DEAL_SUMMARY_SAVE_DRAFT_BUTTON}
				>
					<Box
						onClick={() => {
							navigate(ROUTES.DASHBOARD);
						}}
					>
						<Text variant="p-lg">{tMipCreate('summary.save.paragraph')}</Text>
						<Button
							disabled={isLoading}
							variant="primary"
							icon="chevron-right"
							iconPlacement="right"
							iconSize="lg"
						>
							{tMipCreate('summary.save.button')}
						</Button>
					</Box>
				</UIElementIdentifier>
			</ContentWrapper>
		</div>
	);
};

const WarningWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.5em;
	flex-grow: 1;
	${NotificationBox} {
		flex-grow: 1;
	}
`;

const ContentWrapper = styled.div`
	min-height: 17.6rem;
	display: flex;
	gap: 1.5em;
`;

const Box = styled.div`
	width: 100%;
	cursor: pointer;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	align-items: flex-start;
	gap: 2em;
	padding: 1.5em 2.25em;
	border: 1px solid ${({ theme }) => theme.v2.color.neutral[400]};
	border-radius: 0.4rem;
`;

const Memoized = memo(DealSummary);
const Styled = styled(Memoized)`
	display: flex;
	flex-direction: column;

	${ContentWrapper} {
		margin-top: 2em;
	}
`;

export { Styled as DealSummary };
